import { logger } from '@gik/analytics/utils/logger';
import { useInkind } from '@gik/api/inkinds/inkind';
import { useUser } from '@gik/api/users/user';
import { useUserStore } from '@gik/core/store/UserStore';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { useHideIntercomDefaultLauncher } from '@gik/core/utils/Intercom';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { LoadingSpinner } from '@gik/ui/LoadingSpinner';
import { UI } from '@gik/ui/UIManager';
import React from 'react';
import { sendThankYouMessage } from '../../api/ThankYouMessages';
import type { ThankYouFormProps, ThankYouFormValues } from './ThankYouForm';
import { ThankYouForm } from './ThankYouForm';

export type ThankYouFormWrapperProps = {
  buttonsPortal?: React.MutableRefObject<HTMLElement>;
  inkindRouteId: string;
  onSuccess?: () => void;
  onFail?: () => void;
} & ThankYouFormProps;

function ThankYouFormWrapperComp({
  className,
  buttonsPortal,
  sender,
  recipient,
  inkindRouteId,
  orderId,
  claimId,
  onSuccess,
  onFail,
  ...otherProps
}: React.PropsWithChildren<ThankYouFormWrapperProps>): React.ReactElement {
  const bem = useBemCN('thank-you-form-wrapper');
  useHideIntercomDefaultLauncher();

  const [values, setValues] = React.useState<ThankYouFormValues>();

  const userId = useUserStore(state => state.id);
  const { data: inkind } = useInkind(inkindRouteId);
  const { data: user } = useUser(userId);

  const handleSubmit = React.useCallback(
    async (values: ThankYouFormValues) => {
      try {
        if (orderId || claimId) {
          const response = await sendThankYouMessage({ inkindRouteId, orderId, claimId, payload: values });

          if (response.ok) {
            UI.notifySuccess('Thank you sent');
            onSuccess?.();
          } else {
            UI.notifyError('Failed to send Thank you');
            onFail?.();
          }
        }
      } catch (err) {
        logger.error(err);
      }
    },
    [orderId, claimId, inkindRouteId, onSuccess, onFail]
  );

  React.useEffect(() => {
    if (!user) return;

    setValues({
      greeting: `Dear ${recipient.firstName || recipient.fullName}`,
      message: `Thank you so much for your care and support during this time.`,
      signature: `- Love ${user.firstName}`,
      backgroundColor: 'aqua',
    });
  }, [user, recipient]);

  if (!values) return <LoadingSpinner center />;

  return (
    <div {...bem(null, null, className)}>
      <ThankYouForm
        buttonsPortal={buttonsPortal}
        autoFocus
        initialValues={values}
        onSubmit={handleSubmit}
        sender={sender}
        recipient={recipient}
        orderId={orderId}
        claimId={claimId}
        {...otherProps}
        locked={!inkind?.isPremium}
      />
    </div>
  );
}

export const ThankYouFormWrapper = withComponentErrorBoundary(ThankYouFormWrapperComp);
